// src/components/Activity.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import Loading from './Loading';
import { useLanguage } from '../LanguageContext';

const Activity = ({ embed = false }) => {
    const { t, isRtl } = useLanguage();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchLogs = async () => {
        try {
            const res = await axios.get(`${API_BASE}/activity-logs`);
            setLogs(res.data || []);
        } catch (err) {
            setError(t.failed_fetch_logs);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const formatTime = (ts) => {
        const d = new Date(ts);
        return d.toLocaleString(isRtl ? 'ar-EG' : 'en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActionColor = (action) => {
        switch (action) {
            case 'CREATE': return '#1976d2';
            case 'UPDATE': return '#1a73e8';
            case 'DELETE': return '#d82c0d';
            case 'LOGIN': return '#5c6ac4';
            default: return '#6d7175';
        }
    };

    return (
        <div className="fade-in" style={{ maxWidth: 900, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 4, height: 28, borderRadius: 4, background: '#1976d2' }} />
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1a1c1d' }}>{t.activity_history}</h1>
                </div>
            </div>

            {error && <div style={{ color: '#d82c0d', background: '#fff1f0', padding: '12px 16px', borderRadius: 8, marginBottom: 20 }}>{error}</div>}

            <div style={{
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #ebebeb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                minHeight: 400,
                position: 'relative'
            }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#6d7175' }}>{t.latest_actions}</span>
                    <button onClick={() => { setLoading(true); fetchLogs(); }} style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{t.refresh}</button>
                </div>

                {loading ? (
                    <div style={{ padding: '100px 0' }}>
                        <Loading type="component" message={t.updating_logs} />
                    </div>
                ) : logs.length === 0 ? (
                    <div className="fade-in" style={{ padding: 60, textAlign: 'center', color: '#6d7175' }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: 16, opacity: 0.3 }}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                        <p>{t.no_activities_recorded}</p>
                    </div>
                ) : (
                    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column' }}>
                        {logs.map((log, idx) => (
                            <div key={log._id} style={{
                                padding: '16px 20px',
                                borderBottom: idx === logs.length - 1 ? 'none' : '1px solid #f9f9f9',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 16,
                                transition: 'background 0.2s'
                            }} onMouseOver={e => e.currentTarget.style.background = '#fcfcfc'} onMouseOut={e => e.currentTarget.style.background = '#fff'}>
                                <div style={{
                                    minWidth: 40, height: 40, borderRadius: 20,
                                    background: `${getActionColor(log.action)}15`,
                                    color: getActionColor(log.action),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 700, fontSize: 12
                                }}>
                                    {log.action.charAt(0)}
                                </div>
                                <div style={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                                    <div style={{ fontSize: 14, color: '#1a1c1d', fontWeight: 500, lineHeight: 1.5 }}>
                                        {log.description}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                        <div style={{ fontSize: 12, color: '#6d7175' }}>{formatTime(log.timestamp)}</div>
                                        <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#d2d2d2' }} />
                                        <div style={{ fontSize: 11, color: '#919191', textTransform: 'uppercase', letterSpacing: 0.5 }}>{log.target}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Activity;
